import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/components/layout/AppLayout';

// Public
import { LandingPage } from '@/pages/public/LandingPage';
import { PricingPage } from '@/pages/public/PricingPage';
import { DownloadPage } from '@/pages/public/DownloadPage';

// Auth
import { LoginPage } from '@/pages/auth/LoginPage';
import { SignupPage } from '@/pages/auth/SignupPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { AcceptInvitePage } from '@/pages/auth/AcceptInvitePage';
import { PartnerAcceptInvitePage } from '@/pages/auth/PartnerAcceptInvitePage';

// Onboarding
import { OnboardingPage } from '@/pages/dashboard/OnboardingPage';

// App
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { TvsPage } from '@/pages/tvs/TvsPage';
import { TvDetailPage } from '@/pages/tvs/TvDetailPage';
import { TvSessionsPage } from '@/pages/tvs/TvSessionsPage';
import { AddTvPage } from '@/pages/tvs/AddTvPage';
import { PlaylistsPage } from '@/pages/playlists/PlaylistsPage';
import { PlaylistEditorPage } from '@/pages/playlists/PlaylistEditorPage';
import { TemplatesGalleryPage } from '@/pages/playlists/TemplatesGalleryPage';
import { ThemesPage } from '@/pages/themes/ThemesPage';
import { ProductsPage } from '@/pages/products/ProductsPage';
import { BillingPage } from '@/pages/billing/BillingPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { StoresPage } from '@/pages/stores/StoresPage';
import { AgentsListPage } from '@/pages/settings/agent/AgentsListPage';
import { RoutinesPage } from '@/pages/settings/agent/RoutinesPage';
import { ExecutionsLogPage } from '@/pages/settings/agent/ExecutionsLogPage';
import { CatalogSyncPage } from '@/pages/settings/agent/CatalogSyncPage';

// Ajuda
import { HelpCenterPage } from '@/help/HelpCenterPage';

// Admin
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { AdminTenantsPage } from '@/pages/admin/AdminTenantsPage';
import { AdminPartnersPage } from '@/pages/admin/AdminPartnersPage';

// Parceiro / revenda
import { PartnerLayout } from '@/pages/partner/PartnerLayout';
import { PartnerClientsPage } from '@/pages/partner/PartnerClientsPage';
import { PartnerClientDetailPage } from '@/pages/partner/PartnerClientDetailPage';

/** Rola para o topo ao trocar de rota; respeita âncoras (#secao) na mesma página. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash]);
  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function MasterRoute({ children }: { children: React.ReactNode }) {
  const { isMasterAdmin } = useAuthStore();
  if (!isMasterAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function PartnerRoute({ children }: { children: React.ReactNode }) {
  const { isPartner } = useAuthStore();
  if (!isPartner) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Área do tenant (TVs, playlists...). Parceiro não tem tenant — manda pro /parceiro. */
function TenantArea() {
  const { isPartner } = useAuthStore();
  if (isPartner) return <Navigate to="/parceiro" replace />;
  return <AppLayout />;
}

export default function App() {
  const initialize = useAuthStore((s) => s.initialize);
  useEffect(() => { initialize(); }, [initialize]);

  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/baixar" element={<DownloadPage />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/cadastro" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      <Route path="/aceitar-convite" element={<AcceptInvitePage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/parceiro/aceitar-convite" element={<PartnerAcceptInvitePage />} />

      {/* Parceiro / revenda (autenticado, role=partner) */}
      <Route element={<ProtectedRoute><PartnerRoute><PartnerLayout /></PartnerRoute></ProtectedRoute>}>
        <Route path="/parceiro" element={<PartnerClientsPage />} />
        <Route path="/parceiro/clientes/:id" element={<PartnerClientDetailPage />} />
      </Route>

      {/* App (autenticado) */}
      <Route element={<ProtectedRoute><TenantArea /></ProtectedRoute>}>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        <Route path="/stores" element={<StoresPage />} />
        <Route path="/tvs" element={<TvsPage />} />
        <Route path="/tvs/add" element={<AddTvPage />} />
        <Route path="/tvs/sessions" element={<TvSessionsPage />} />
        <Route path="/tvs/:id" element={<TvDetailPage />} />

        <Route path="/playlists" element={<PlaylistsPage />} />
        <Route path="/playlists/templates" element={<TemplatesGalleryPage />} />
        <Route path="/playlists/new" element={<PlaylistEditorPage />} />
        <Route path="/playlists/:id" element={<PlaylistEditorPage />} />

        <Route path="/themes" element={<ThemesPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/agent"          element={<AgentsListPage />} />
        <Route path="/settings/agent/routines" element={<RoutinesPage />} />
        <Route path="/settings/agent/catalog"  element={<CatalogSyncPage />} />
        <Route path="/settings/agent/logs"     element={<ExecutionsLogPage />} />

        {/* Ajuda */}
        <Route path="/ajuda" element={<HelpCenterPage />} />

        {/* Master Admin */}
        <Route path="/admin" element={<MasterRoute><AdminDashboard /></MasterRoute>} />
        <Route path="/admin/tenants" element={<MasterRoute><AdminTenantsPage /></MasterRoute>} />
        <Route path="/admin/partners" element={<MasterRoute><AdminPartnersPage /></MasterRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
